"use client";

import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import { Box, Card, CardActionArea, Icon, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import NextLink from "next/link";

const HREF = "/products?sort=related&utm_source=zibasi&utm_medium=banner";

const floatBlob = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(8px, -6px) scale(1.05); }
`;

const stepPop = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const ctaPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 142, 250, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(0, 142, 250, 0); }
`;

const pulseRing = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.08); opacity: 0.2; }
`;

type Props = {
  variant: "desktop" | "mobile";
};

function InstallmentSteps({ size }: { size: "desktop" | "mobile" }) {
  const isDesktop = size === "desktop";
  const steps = ["۱", "۲", "۳", "۴"];

  return (
    <Stack direction="row" alignItems="center" spacing={isDesktop ? 1 : 0.55} useFlexGap>
      {steps.map((label, index) => (
        <Box
          key={label}
          aria-hidden
          sx={{
            width: isDesktop ? 44 : 30,
            height: isDesktop ? 44 : 30,
            borderRadius: isDesktop ? 2.5 : 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: isDesktop ? "1.05rem" : "0.78rem",
            color: "#fff",
            bgcolor: index === 3 ? "#0066c7" : "#008efa",
            boxShadow: "0 6px 14px rgba(0, 102, 199, 0.22)",
            animation: `${stepPop} 2.4s ease-in-out infinite`,
            animationDelay: `${index * 0.18}s`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {label}
        </Box>
      ))}
    </Stack>
  );
}

function MobileHeroNumber() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 84,
        height: 84,
        mx: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "2px solid rgba(0, 142, 250, 0.35)",
          animation: `${pulseRing} 2.8s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
      <Box
        sx={{
          width: 68,
          height: 68,
          borderRadius: "50%",
          background: "linear-gradient(145deg, #008efa 0%, #0066c7 100%)",
          boxShadow: "0 12px 24px rgba(0, 102, 199, 0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 900,
          fontSize: "2rem",
          lineHeight: 1,
          letterSpacing: "-0.04em",
        }}
      >
        ۴
      </Box>
    </Box>
  );
}

export default function InstallmentBentoCard({ variant }: Props) {
  const isDesktop = variant === "desktop";

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: isDesktop ? undefined : 200,
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        isolation: "isolate",
        background: "linear-gradient(145deg, #E8F5FF 0%, #C8E8FF 48%, #8FD0FF 100%)",
      }}
    >
      <CardActionArea
        component={NextLink}
        href={HREF}
        aria-label="مشاهده محصولات با پرداخت اقساطی"
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: isDesktop ? "center" : "stretch",
          justifyContent: isDesktop ? "center" : "space-between",
          gap: isDesktop ? 1.5 : 0,
          position: "relative",
          overflow: "hidden",
          px: isDesktop ? 2 : 0,
          py: isDesktop ? 2 : 0,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: isDesktop ? -36 : -30,
            right: isDesktop ? -28 : -24,
            width: isDesktop ? 160 : 110,
            height: isDesktop ? 160 : 110,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.45)",
            animation: `${floatBlob} 5.5s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: isDesktop ? -50 : -36,
            left: isDesktop ? -40 : -28,
            width: isDesktop ? 180 : 120,
            height: isDesktop ? 180 : 120,
            borderRadius: "50%",
            bgcolor: "rgba(0, 142, 250, 0.18)",
            animation: `${floatBlob} 6.5s ease-in-out infinite reverse`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />

        {isDesktop ? (
          <Stack
            spacing={1.25}
            alignItems="center"
            textAlign="center"
            sx={{ position: "relative", zIndex: 1, minWidth: 0, width: "100%" }}
          >
            <InstallmentSteps size="desktop" />

            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: "grey.900", lineHeight: 1.3 }}>
                پرداخت ۴ قسطه
              </Typography>
              <Typography variant="h6" fontWeight={500} sx={{ color: "#0066c7", lineHeight: 1.4, mt: 0.25 }}>
                بدون کارمزد
              </Typography>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 300,
                bgcolor: "#008efa",
                color: "#fff",
                animation: `${ctaPulse} 2.4s ease-in-out infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            >
              <Typography variant="body1" sx={{ color: "inherit", fontWeight: 700 }}>
                مشاهده محصولات
              </Typography>
              <Icon sx={{ color: "inherit" }}>
                <ArrowLeftIcon />
              </Icon>
            </Box>
          </Stack>
        ) : (
          <>
            {/* Visual hero — mirrors Wheel card structure */}
            <Box sx={{ position: "relative", zIndex: 1, pt: 1.75, px: 1 }}>
              <MobileHeroNumber />
            </Box>

            <Stack
              spacing={0.5}
              sx={{
                position: "relative",
                zIndex: 1,
                mt: "auto",
                p: 1.5,
                pt: 0.75,
              }}
            >
              <Typography fontWeight={800} sx={{ color: "grey.900", lineHeight: 1.25, fontSize: "0.98rem" }}>
                پرداخت ۴ قسطه
              </Typography>
              <Typography fontWeight={600} sx={{ color: "#0066c7", lineHeight: 1.3, fontSize: "0.8rem" }}>
                بدون کارمزد
              </Typography>

              <Stack direction="row" sx={{ pt: 0.4 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.2,
                    py: 0.55,
                    borderRadius: 300,
                    bgcolor: "#008efa",
                    color: "#fff",
                    animation: `${ctaPulse} 2.4s ease-in-out infinite`,
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                  }}
                >
                  <Typography variant="button" sx={{ color: "inherit", fontWeight: 700, fontSize: "0.78rem" }}>
                    مشاهده
                  </Typography>
                  <Icon fontSize="small" sx={{ color: "inherit" }}>
                    <ArrowLeftIcon />
                  </Icon>
                </Box>
              </Stack>
            </Stack>
          </>
        )}
      </CardActionArea>
    </Card>
  );
}
